import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useVoice } from "@/context/VoiceContext";
import { scrollToId } from "@/lib/scroll";
import { assets, identity } from "@/config";

const NAV = [
  { id: "home", label: "Home" },
  { id: "cases", label: "Cases" },
  { id: "evidence", label: "Evidence" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const { setChatOpen } = useVoice();
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-40 border-b backdrop-blur-md transition-[background-color,border-color] duration-500 ${
        solid ? "border-line bg-noir/95" : "border-cream/10 bg-noir/70"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8" aria-label="Primary">
        <button data-testid="nav-logo" onClick={() => go("home")} aria-label={`${identity.brand} — home`} className="flex items-center">
          <img src={assets.logo} alt={identity.brand} className="h-9 w-auto sm:h-10" />
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)} className="text-sm text-gray2 transition-colors hover:text-cream">
              {n.label}
            </button>
          ))}
          <button
            data-testid="nav-ask-andrew"
            onClick={() => setChatOpen(true)}
            className="rounded-full border border-cream/20 px-4 py-1.5 text-sm text-cream/90 transition-colors hover:border-cream/40 hover:text-cream"
          >
            Ask Andrew
          </button>
        </div>

        <button
          data-testid="nav-hamburger"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="flex h-10 w-10 items-center justify-center text-cream md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.22 }}
            className="flex flex-col gap-5 border-t border-cream/10 bg-noir/95 px-5 py-5 backdrop-blur-md md:hidden"
          >
            {NAV.map((n) => (
              <button key={n.id} data-testid={`mobile-nav-${n.id}`} onClick={() => go(n.id)} className="text-left text-base text-gray2 hover:text-cream">
                {n.label}
              </button>
            ))}
            <button data-testid="mobile-ask-andrew" onClick={() => { setOpen(false); setChatOpen(true); }} className="border-t border-cream/10 pt-4 text-left text-base text-cream">
              Ask Andrew
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
